/**
 * Kick Chat WebSocket bağlantısı ve komut işlemlerini yöneten servis
 */
export class KickChatService {
  private user: string;
  private chatroomId: string | null = null;
  private channelId: string | null = null;
  private websocket: WebSocket | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private readonly reconnectDelay = 5000;

  // Callback fonksiyonları
  private onAmountChange?: (amountTRY: number, previousAmountTRY: number) => void;
  private onConnectionChange?: (connected: boolean, message?: string) => void;
  private onStatusShow?: () => void;
  private onHelpShow?: () => void;
  private onUndo?: () => void;

  // Cooldown sistemi
  private lastStatusTime = 0;
  private statusCooldownMs = 10 * 1000; // 10 saniye (test için - production'da 3 * 60 * 1000)

  // Para birimi dönüşüm kuru
  private readonly JPY_TO_TRY_RATE = 0.28; // 1 JPY ≈ ₺0.28

  constructor(user: string) {
    if (!user) throw new Error("Kick chat için kullanıcı adı gerekli!");
    this.user = user;
  }

  /**
   * Callback'leri ayarla
   */
  setCallbacks(callbacks: {
    onAmountChange?: (amountTRY: number, previousAmountTRY: number) => void;
    onConnectionChange?: (connected: boolean, message?: string) => void;
    onStatusShow?: () => void;
    onHelpShow?: () => void;
    onUndo?: () => void;
  }) {
    this.onAmountChange = callbacks.onAmountChange;
    this.onConnectionChange = callbacks.onConnectionChange;
    this.onStatusShow = callbacks.onStatusShow;
    this.onHelpShow = callbacks.onHelpShow;
    this.onUndo = callbacks.onUndo;
  }

  /**
   * HTTP isteği yap (retry logic ile)
   */
  private async fetchData(url: string, options: RequestInit = {}): Promise<Response | null> {
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          console.error(`Fetch hatası: ${response.status} (${url}) (Deneme ${attempt})`);
          if (response.status === 404) return null;
          if (attempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, this.reconnectDelay));
          }
        } else {
          return response;
        }
      } catch (error) {
        console.error(`Ağ hatası (${url}) (Deneme ${attempt}):`, error);
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, this.reconnectDelay));
        }
      }
    }
    console.error(`Fetch işlemi ${maxAttempts} denemeden sonra başarısız: ${url}`);
    return null;
  }

  /**
   * Kullanıcı bilgilerini al (chatroom ve channel ID)
   */
  private async getUserInfo(): Promise<boolean> {
    console.log(`Kullanıcı bilgileri alınıyor: ${this.user}`);
    this.onConnectionChange?.(false, "Kullanıcı bilgileri alınıyor...");

    const response = await this.fetchData(`https://kick.com/api/v2/channels/${this.user}`);
    if (response) {
      try {
        const data = await response.json() as any;
        if (data?.chatroom?.id && data?.chatroom?.channel_id) {
          this.chatroomId = data.chatroom.id;
          this.channelId = data.chatroom.channel_id;
          console.log(`Bilgiler alındı: Chatroom ID=${this.chatroomId}, Channel ID=${this.channelId}`);
          return true;
        } else {
          console.error("Kullanıcı bilgileri alınsa da chatroom verisi eksik:", data);
          this.onConnectionChange?.(false, "Chatroom bilgileri eksik!");
          return false;
        }
      } catch (error) {
        console.error("JSON ayrıştırma hatası:", error);
        this.onConnectionChange?.(false, "Veri ayrıştırma hatası!");
        return false;
      }
    } else {
      console.error(`Kullanıcı bilgileri alınamadı: ${this.user}`);
      this.onConnectionChange?.(false, `Kullanıcı "${this.user}" bulunamadı!`);
      return false;
    }
  }

  /**
   * WebSocket bağlantısını kur
   */
  private connectToChatroom(): void {
    if (!this.chatroomId || !this.channelId) {
      console.error("Chatroom ve Channel ID olmadan bağlanamıyoruz!");
      return;
    }

    if (this.websocket &&
      (this.websocket.readyState === WebSocket.OPEN ||
        this.websocket.readyState === WebSocket.CONNECTING)) {
      console.log("WebSocket zaten açık ya da bağlanıyor.");
      return;
    }

    const wsUrl = "wss://ws-us2.pusher.com/app/34bf7a0ff419a2a775b9?protocol=7&client=js&version=8.4.0-rc2&flash=false";
    console.log(`WebSocket'a bağlanılıyor: ${wsUrl}`);
    this.onConnectionChange?.(false, "Bağlanıyor...");

    try {
      this.websocket = new WebSocket(wsUrl);

      this.websocket.onopen = () => {
        console.log("WebSocket bağlantısı kuruldu.");
        this.onConnectionChange?.(true, "Bağlandı!");

        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
        }

        this.subscribeToChannel(`chatrooms.${this.chatroomId}.v2`);
        this.subscribeToChannel(`channel.${this.channelId}`);
        this.startPing();
      };

      this.websocket.onmessage = event => this.parseMessage(event.data);

      this.websocket.onerror = err => {
        console.error("WebSocket hatası:", err);
        this.onConnectionChange?.(false, "Bağlantı hatası!");
      };

      this.websocket.onclose = event => {
        console.log(`WebSocket kapandı. Code=${event.code}, Reason=${event.reason || "Belirtilmemiş"}`);
        this.onConnectionChange?.(false, "Bağlantı koptu. Tekrar bağlanılıyor...");
        this.stopPing();
        // Her durumda yeniden bağlan
        this.scheduleReconnect();
      };
    } catch (error) {
      console.error("WebSocket oluşturulamadı:", error);
      this.onConnectionChange?.(false, "WebSocket başlangıç hatası!");
      this.scheduleReconnect();
    }
  }

  /**
   * Yeniden bağlanmayı planla
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimeout) return;
    this.websocket = null;
    console.log(`Yeniden bağlanma ${this.reconnectDelay / 1000} saniye içinde denenecek...`);
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      console.log("Yeniden bağlanılıyor...");
      this.connectToChatroom();
    }, this.reconnectDelay);
  }

  /**
   * Kanala abone ol
   */
  private subscribeToChannel(channelName: string): void {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      console.log(`Kanal abonesi gönderiliyor: ${channelName}`);
      this.websocket.send(JSON.stringify({
        event: "pusher:subscribe",
        data: { auth: null, channel: channelName }
      }));
    } else {
      console.warn(`WebSocket açık değil, ${channelName} kanalına abone olunamıyor.`);
    }
  }

  /**
   * WebSocket mesajını ayrıştır
   */
  private parseMessage(message: string): void {
    try {
      const msg = JSON.parse(message);
      // Pusher'ın dahili mesajlarını atla
      if (msg.event === "pusher:pong" || msg.event?.startsWith("pusher_internal:")) return;

      if (msg.data) {
        // msg.data string veya object olabilir
        const data = typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data;
        if (msg.event === "App\\Events\\ChatMessageEvent") {
          this.handleChatMessage(data);
        }
      }
    } catch (error) {
      console.error("WebSocket mesajı ayrıştırılırken hata:", error);
    }
  }

  /**
   * Chat mesajını işle
   */
  private handleChatMessage(data: any): void {
    const msgContent = data?.content?.trim();
    const senderUsername = data?.sender?.username;
    const badges = data?.sender?.identity?.badges;

    if (!msgContent || !senderUsername || !Array.isArray(badges)) return;

    // Yetkili kullanıcı kontrolü: "broadcaster" veya "moderator"
    const isPrivileged = badges.some((badge: any) =>
      badge.type === "broadcaster" || badge.type === "moderator"
    );

    // Durum komutu herkes kullanabilir
    if (msgContent.match(/^!(harcama|test)\s+(durum|status)$/i)) {
      console.log(`Durum komutu: ${senderUsername} -> ${msgContent}`);
      this.handleStatusCommand();
      return;
    }

    // Diğer komutlar sadece yetkili kullanıcılar için
    if (isPrivileged) {
      this.handleModeratorCommand(msgContent, senderUsername);
    }
  }

  /**
   * Moderatör komutlarını işle
   */
  private handleModeratorCommand(command: string, username: string): void {
    console.log(`Moderatör komutu: ${username} -> ${command}`);

    // !harcama try 100 veya !test try 100
    const tryMatch = command.match(/^!(harcama|test)\s+(try|tl|₺)\s+(\d+(?:\.\d+)?)$/i);
    if (tryMatch && tryMatch[3]) {
      const amount = parseFloat(tryMatch[3]);
      console.log(`TRY ekleniyor: ${amount}`);
      this.addTRY(amount);
      return;
    }

    // !harcama yen/jpy/¥ 1000 veya !test yen 500
    const jpyMatch = command.match(/^!(harcama|test)\s+(yen|jpy|¥)\s+(\d+(?:\.\d+)?)$/i);
    if (jpyMatch && jpyMatch[3]) {
      const jpyAmount = parseFloat(jpyMatch[3]);
      const tryAmount = jpyAmount * this.JPY_TO_TRY_RATE; // 1 JPY ≈ ₺0.28
      console.log(`JPY ekleniyor: ¥${jpyAmount} = ₺${tryAmount.toFixed(2)}`);
      this.addTRY(tryAmount);
      return;
    }

    // !harcama undo veya !test undo
    if (command.match(/^!(harcama|test)\s+undo$/i)) {
      console.log(`Undo komutu`);
      this.onUndo?.();
      return;
    }

    // !harcama veya !test (yardım göster)
    if (command.match(/^!(harcama|test)$/i)) {
      console.log(`Yardım komutu`);
      this.onHelpShow?.();
      return;
    }
  }

  /**
   * Durum komutunu işle (herkes kullanabilir)
   */
  private handleStatusCommand(): void {
    this.showStatus();
  }

  /**
   * TRY ekle
   */
  private addTRY(amount: number): void {
    const currentAmount = this.getCurrentAmount();
    const newAmount = currentAmount + amount;
    this.saveAmount(newAmount);
    console.log(`₺${amount} eklendi. Toplam: ₺${newAmount.toFixed(2)}`);
    this.onAmountChange?.(newAmount, currentAmount);
  }



  /**
   * Miktarı sıfırla
   */
  private resetAmount(): void {
    const currentAmount = this.getCurrentAmount();
    this.saveAmount(0);
    console.log(`Miktar sıfırlandı. Önceki: ₺${currentAmount.toFixed(2)}`);
    this.onAmountChange?.(0, currentAmount);
  }

  /**
   * Miktarı belirli bir değere ayarla
   */
  private setAmount(amount: number): void {
    const currentAmount = this.getCurrentAmount();
    this.saveAmount(amount);
    console.log(`Miktar ayarlandı: ₺${amount.toFixed(2)} (Önceki: ₺${currentAmount.toFixed(2)})`);
    this.onAmountChange?.(amount, currentAmount);
  }

  /**
   * Durum göster (3 dakika cooldown ile)
   */
  private showStatus(): void {
    const currentTime = Date.now();
    const timeSinceLastStatus = currentTime - this.lastStatusTime;

    // Cooldown kontrolü
    if (this.lastStatusTime > 0 && timeSinceLastStatus < this.statusCooldownMs) {
      const remainingTime = Math.ceil((this.statusCooldownMs - timeSinceLastStatus) / 1000);
      console.log(`Durum komutu cooldown'da. ${remainingTime} saniye sonra tekrar deneyin.`);
      return;
    }

    // Durum bilgilerini göster
    const currentAmount = this.getCurrentAmount();
    console.log(`Durum overlay gösteriliyor: ₺${currentAmount.toFixed(2)}`);

    // Overlay'i göster
    this.onStatusShow?.();

    // Cooldown'u güncelle
    this.lastStatusTime = currentTime;
    console.log(`Durum komutu kullanıldı. ${this.statusCooldownMs / 1000} saniye sonra tekrar kullanılabilir.`);
  }

  /**
   * Mevcut miktarı localStorage'dan al
   */
  private getCurrentAmount(): number {
    try {
      const saved = localStorage.getItem('kick-overlay-amount');
      return saved ? parseFloat(saved) : 0;
    } catch (error) {
      console.error("localStorage okuma hatası:", error);
      return 0;
    }
  }

  /**
   * Miktarı localStorage'a kaydet
   */
  private saveAmount(amount: number): void {
    try {
      localStorage.setItem('kick-overlay-amount', amount.toString());
    } catch (error) {
      console.error("localStorage yazma hatası:", error);
    }
  }

  /**
   * Ping gönder
   */
  private startPing(): void {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.send(JSON.stringify({ event: "pusher:ping", data: {} }));
      } else {
        this.stopPing();
      }
    }, 60000);
  }

  /**
   * Ping'i durdur
   */
  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Servisi başlat
   */
  async initialize(): Promise<void> {
    if (!this.user) {
      console.error("Başlatma hatası: Kullanıcı adı eksik.");
      this.onConnectionChange?.(false, "Hata: Kullanıcı adı eksik!");
      return;
    }

    const success = await this.getUserInfo();
    if (success) {
      this.connectToChatroom();
    } else {
      console.error("Başlatma hatası: Kullanıcı bilgileri alınamadı.");
    }
  }

  /**
   * Mevcut kaydedilmiş miktarı döndür (başlangıç için)
   */
  getInitialAmount(): number {
    return this.getCurrentAmount();
  }

  /**
   * Bağlantıyı kapat
   */
  disconnect(): void {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    this.stopPing();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
}
